import { Autocomplete, Stack, TextField } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MONTHS } from '../../constants/months';

const MonthYearGroupedSelector = ({
  availableDates,
  selectedMonth,
  selectedYear,
  onMonthYearChange,
  disabled = false,
  isLoading = false,
  error = null,
}) => {
  const [selectedValue, setSelectedValue] = useState(null);

  const groupedOptions = useMemo(() => {
    if (!availableDates || typeof availableDates !== 'object') {
      return [];
    }

    const options = [];

    const sortedYears = Object.keys(availableDates)
      .map((year) => parseInt(year))
      .sort((a, b) => a - b);

    sortedYears.forEach((year) => {
      const monthsForYear = availableDates[year];

      if (monthsForYear && monthsForYear.length > 0) {
        monthsForYear.forEach((monthValue) => {
          const month = MONTHS.find((m) => m.value === monthValue);
          if (month) {
            options.push({
              value: `${monthValue}-${year}`,
              label: `${month.label} ${year}`,
              month: monthValue,
              year: year,
              group: year.toString(),
            });
          }
        });
      }
    });

    return options;
  }, [availableDates]);

  useEffect(() => {
    if (selectedMonth && selectedYear) {
      const option = groupedOptions.find(
        (opt) => opt.month === selectedMonth && opt.year === selectedYear
      );

      // Solo actualizar si el valor es diferente para evitar loops infinitos
      if (option && (!selectedValue || selectedValue.value !== option.value)) {
        setSelectedValue(option);
        // Solo llamar onMonthYearChange si no es la primera carga
        if (selectedValue !== null) {
          onMonthYearChange(option.month, option.year);
        }
      } else if (!option && selectedValue) {
        // Si no se encuentra la opción pero hay un valor seleccionado, limpiar
        setSelectedValue(null);
      }
    } else if (selectedValue) {
      // Si no hay mes/año seleccionado pero hay valor en el componente, limpiar
      setSelectedValue(null);
    }
  }, [selectedMonth, selectedYear, groupedOptions, selectedValue, onMonthYearChange]);

  const handleChange = useCallback(
    (_, newValue) => {
      setSelectedValue(newValue);

      if (newValue) {
        onMonthYearChange(newValue.month, newValue.year);
      } else {
        onMonthYearChange(null, null);
      }
    },
    [onMonthYearChange]
  );

  const groupBy = useCallback((option) => option.group, []);

  const renderGroup = useCallback(
    (params) => (
      <li key={`${params.group}-${params.children?.length || 0}`}>
        <div
          style={{
            padding: '8px 16px',
            fontWeight: 500,
            color: '#666',
            backgroundColor: '#f5f5f5',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          {params.group}
        </div>
        <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
      </li>
    ),
    []
  );

  const getOptionLabel = useCallback((option) => {
    if (typeof option === 'string') return option;
    return option.label || '';
  }, []);

  const isOptionEqualToValue = useCallback((option, value) => {
    if (!option || !value) return false;
    return option.value === value.value;
  }, []);

  const hasNoDates = !availableDates || Object.keys(availableDates).length === 0;

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Stack sx={{ width: '100%' }}>
        <h3 style={{ fontSize: 12, color: '#4f4f4f' }}>¿Cuándo viajas?</h3>
        <Autocomplete
          id='month-year-autocomplete'
          className='form__field month-year-typeahead'
          noOptionsText={isLoading ? 'Cargando...' : 'No hay fechas disponibles'}
          size='small'
          value={selectedValue}
          onChange={handleChange}
          options={groupedOptions}
          groupBy={groupBy}
          renderGroup={renderGroup}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          disabled={disabled || isLoading}
          loading={isLoading}
          loadingText='Cargando fechas disponibles...'
          style={{ width: '100%' }}
          clearOnBlur
          ListboxProps={{
            style: { maxHeight: '400px' },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                isLoading
                  ? 'Cargando fechas...'
                  : hasNoDates
                    ? 'No hay fechas disponibles'
                    : 'Selecciona mes y año'
              }
              className='focus:ring-offset-0 focus:ring-0 ring-offset-0 ring-transparent'
              InputProps={{
                ...params.InputProps,
                style: {
                  ...params.InputProps.style,
                  backgroundColor: isLoading ? '#f5f5f5' : 'white',
                  cursor: isLoading ? 'wait' : 'text',
                },
              }}
            />
          )}
        />
      </Stack>
    </Stack>
  );
};

export default MonthYearGroupedSelector;
